/**
 * SubscriptionPage — show plans, current plan, usage and upgrade request.
 * No payment integration in this MVP — clicking "Upgrade" records intent
 * and the sales team follows up.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, TrendingUp, Zap, Sparkles, Crown, BarChart3 } from "lucide-react";
import { RudrikLogo } from "../components/RudrikLogo";

const API = `${window.location.origin}/api`;

const PLAN_ORDER = ["trial", "starter", "business", "enterprise"];
const PLAN_ICONS = { trial: Sparkles, starter: Zap, business: TrendingUp, enterprise: Crown };
const PLAN_COLORS = {
  trial: "#f59e0b", starter: "#22d3ee",
  business: "#a855f7", enterprise: "#22c55e",
};

export default function SubscriptionPage() {
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [plansRes, tenantRes] = await Promise.all([
        axios.get(`${API}/plans`, { withCredentials: true }),
        axios.get(`${API}/tenants/me`, { withCredentials: true }),
      ]);
      setPlans(plansRes.data.plans || []);
      setTenant(tenantRes.data);
      // Usage call — best effort
      if (tenantRes.data?.tenant_id && !tenantRes.data?.legacy) {
        try {
          const usageRes = await axios.get(
            `${API}/tenants/${tenantRes.data.tenant_id}/usage`,
            { withCredentials: true });
          setUsage(usageRes.data);
        } catch { /* ignore */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load subscription info");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const requestUpgrade = async (planKey) => {
    setUpgrading(planKey);
    try {
      const { data } = await axios.post(`${API}/plans/upgrade`,
        { plan: planKey }, { withCredentials: true });
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upgrade request failed");
    } finally { setUpgrading(null); }
  };

  const currentPlan = tenant?.plan || "trial";
  const pendingPlan = tenant?.pending_plan;

  return (
    <div data-testid="subscription-page" style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <RudrikLogo size={40} />
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#f1f5f9", margin: 0 }}>
            Subscription & Billing
          </h1>
          <p style={{ color: "#94a3b8", fontSize: 13, marginTop: 4, marginBottom: 0 }}>
            You're on <strong style={{ color: PLAN_COLORS[currentPlan] }}>
            {(plans.find((p) => p.key === currentPlan) || {}).label || "Free Trial"}</strong>
            {tenant?.trial_ends_at && currentPlan === "trial" &&
              <> · Trial ends {new Date(tenant.trial_ends_at).toLocaleDateString()}</>}
            {pendingPlan && <> · <span style={{ color: "#fbbf24" }}>Upgrade to {pendingPlan} requested</span></>}
          </p>
        </div>
      </div>

      {/* Usage block */}
      {usage && usage.limits && (
        <div data-testid="usage-block" style={{ background: "#0d1117", border: "1px solid #1e2530",
          borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <BarChart3 size={14} color="#22d3ee" />
            <span style={{ color: "#64748b", fontSize: 11, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: 0.7 }}>Current Usage</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
            <UsageBar label="KYC checks"  used={usage.usage.kyc_count} limit={usage.limits.kyc_per_month} />
            <UsageBar label="AML screenings" used={usage.usage.aml_count} limit={usage.limits.aml_per_month} />
            <UsageBar label="API calls"   used={usage.usage.api_calls}  limit={usage.limits.api_calls} />
            <UsageBar label="Team users"  used={usage.usage.users}      limit={usage.limits.users} />
          </div>
        </div>
      )}

      {/* Plans grid */}
      <div data-testid="plans-grid" style={{ display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
        {(loading ? PLAN_ORDER : PLAN_ORDER.filter((k) => plans.find((p) => p.key === k))).map((k) => {
          const p = plans.find((pp) => pp.key === k) || { key: k, label: k, price_inr: 0 };
          return (
            <PlanCard key={k} plan={p} current={currentPlan === k}
              pending={pendingPlan === k}
              onUpgrade={() => requestUpgrade(k)}
              upgrading={upgrading === k} />
          );
        })}
      </div>
    </div>
  );
}

function UsageBar({ label, used, limit }) {
  const unlimited = limit === -1;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));
  const color = pct >= 90 ? "#ef4444" : pct >= 70 ? "#f59e0b" : "#22d3ee";
  return (
    <div data-testid={`usage-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ color: "#cbd5e1", fontSize: 12, fontWeight: 600 }}>{label}</span>
        <span style={{ color: "#f1f5f9", fontSize: 12, fontWeight: 700,
          fontFamily: "ui-monospace, monospace" }}>
          {used.toLocaleString()} / {unlimited ? "∞" : limit.toLocaleString()}
        </span>
      </div>
      <div style={{ height: 6, background: "#080c12", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color,
          transition: "width 0.3s" }} />
      </div>
    </div>
  );
}

function PlanCard({ plan, current, pending, onUpgrade, upgrading }) {
  const Icon = PLAN_ICONS[plan.key] || Sparkles;
  const color = PLAN_COLORS[plan.key] || "#22d3ee";
  const features = [
    plan.kyc_per_month === -1 ? "Unlimited KYC" : `${plan.kyc_per_month?.toLocaleString()} KYC / month`,
    plan.aml_per_month === -1 ? "Unlimited AML" : `${plan.aml_per_month?.toLocaleString()} AML / month`,
    plan.users === -1 ? "Unlimited users" : `${plan.users} team users`,
    plan.api_calls === -1 ? "Unlimited API" : `${plan.api_calls?.toLocaleString()} API calls`,
    plan.key === "enterprise" && "SSO · Custom roles · Dedicated CSM",
    plan.key === "business" && "Webhooks · Bulk screening",
    plan.key === "starter" && "Standard support",
    plan.key === "trial" && "Full features for 14 days",
  ].filter(Boolean);

  return (
    <div data-testid={`plan-card-${plan.key}`} style={{
      background: current ? `${color}10` : "#0d1117",
      border: `1px solid ${current ? color : "#1e2530"}`,
      borderRadius: 12, padding: 20, position: "relative",
    }}>
      {current && (
        <div style={{ position: "absolute", top: -10, right: 16,
          background: color, color: "#0a0d12", padding: "3px 10px",
          borderRadius: 4, fontSize: 10, fontWeight: 800,
          textTransform: "uppercase", letterSpacing: 0.6 }}>Current</div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <Icon size={20} color={color} />
        <div style={{ fontSize: 16, fontWeight: 800, color: "#f1f5f9" }}>{plan.label}</div>
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: "#f1f5f9", marginBottom: 4 }}>
        {plan.key === "trial" && "Free"}
        {plan.key === "enterprise" && "Custom"}
        {plan.key !== "trial" && plan.key !== "enterprise" && (
          <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 600 }}>Contact for pricing</span>
        )}
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: "16px 0", lineHeight: 1.9 }}>
        {features.map((f, i) => (
          <li key={i} style={{ color: "#cbd5e1", fontSize: 12,
            display: "flex", alignItems: "center", gap: 8 }}>
            <Check size={12} color={color} /> {f}
          </li>
        ))}
      </ul>
      {!current && (
        <button onClick={onUpgrade} disabled={upgrading || pending}
          data-testid={`upgrade-${plan.key}`}
          style={{ width: "100%", background: pending ? "#1e2530" : color,
            color: pending ? "#94a3b8" : "#0a0d12", border: "none",
            borderRadius: 8, padding: "10px", fontSize: 12, fontWeight: 800,
            cursor: pending || upgrading ? "not-allowed" : "pointer" }}>
          {pending ? "Requested" : upgrading ? "…" :
            plan.key === "enterprise" ? "Contact sales" : "Upgrade"}
        </button>
      )}
    </div>
  );
}
